import app from 'app';
import MainStoryView from '../views/Story/wtMainStoryView.vue';
import TimelineGatewayController from './TimelineGatewayController';
import FullPreview from 'v/FullPreview/FullPreview.vue';
import StoryboardFullPreview from 'v/FullPreview/StoryboardFullPreview.vue';
import Marionette from 'backbone.marionette';
import storyViewBus from '../views/Story/storyViewBus';
import _ from 'lodash-extended';
import ModuleStory from '../store/module/story.vuex';
import { DuplicateController } from 'c/DuplicateController';
import StoryMetadataView from 'v/metadataEditor/StoryMetadataView';
import { findUpperBoundIndexInSorted } from '#/worker/toolkit';
import { workPool } from '@wochitnpm/helpers';

/**
 * Wrapper over functions that lead to story finish at promise resolve
 * @param fn
 * @returns {Function}
 */
const finishStory = (function () {
  let invocationCounter = 0;

  return function (fn) {
    return function (/* arguments*/) {
      ++invocationCounter;

      // `this` here is instance of `app.storyController`
      if (_.isEmpty(this.story)) {
        app.doctorController.report('finishStory invoked on empty story');
        return $.Deferred().reject();
      } else {
        // cancel autosave logic
        if (this.story.options.editable) {
          this.story.cancelAutosave(true);
          // cancel debounced `hasBeenChanged` function
          this.story.hasBeenChanged.cancel();
        }
      }

      // eslint-disable-next-line prefer-rest-params
      const promise = fn.apply(this, arguments);
      promise.then(
        () => {
          if (_.isEmpty(this.story)) {
            app.doctorController.report('finishStory resolved on empty story');
          } else {
            if (invocationCounter === 1) {
              this.freeStory();
            }
          }
          --invocationCounter;
        },
        () => {
          if (_.isEmpty(this.story)) {
            app.doctorController.report('finishStory rejected on empty story');
          } else {
            // resume autosave logic
            this.story.cancelAutosave(false);
          }
          --invocationCounter;
        }
      );

      return promise;
    };
  };
})();

app.namespace.Controller.StoryController = Marionette.Object.extend({
  initialize: function () {
    this.story = null;
    this.storyView = null;
    this.storyMetadataView = null;
    this._storeUnwatchers = [];

    this.listenTo(app, 'app.menu.command', this._executeMenuCommand);
  },

  freeStory: function () {
    if (!_.isEmpty(this.story)) {
      app.broadcast.emit('sw-story-exit', { storyId: this.story.id });

      if (this.storyView.destroy) {
        this.storyView.destroy();
      } else {
        this.storyView.$destroy();
      }

      app.api.fullPreview.remove(this.story.get('_previewVideoUrl'));
      this.stopListening(this.story);
      this.story.free();
      this.story = null;
      window.story = null;
      app.timelineGateway.destroy();
      app.timelineGateway = null;

      _.each(this._storeUnwatchers, (off) => off());
      app.rootStore.dispatch('STORY_FREE');
    }
  },

  initStory: function ({ story, remoteStash, storyOptions }) {
    storyOptions = _.options(storyOptions, {
      refreshPreview: false,
    });

    return new Promise((resolve, reject) => {
      this.freeStory();
      app.broadcast.emit('sw-story-start', { storyId: story.id });

      // make story available via global scope for QA/Support
      window.story = story;
      this.story = story;
      app.timelineGateway = new TimelineGatewayController();
      this.story.remoteStash = remoteStash;

      app.rootStore.registerModule('story', ModuleStory, {
        preserveState: false,
      });
      app.rootStore
        .dispatch('STORY_INIT', {
          storyModel: story,
          story: story.attributes,
          store: app.rootStore,
          remoteStash,
          plugins: ModuleStory.plugins,
          refreshPreview: storyOptions.refreshPreview,
        })
        .then(() => {
          // object tracking jobs started before the last page load may still be
          // running - pick them back up so their previews get refreshed
          app.objectTrackingController.resumeFromTimeline();

          if (this.story.options.editable) {
            // assuming that at that point current story data is identical to remotely stored story copy
            // after this point story changes can be detectable by autosave
            this.story.persistStory({
              data: this.story.serialize(),
              original: true,
            });

            this.listenTo(this.story, 'story:changed', this._handleStoryChange);
            this._storeUnwatchers.push(
              app.rootStore.watch(
                () => {
                  const { selectedAssets, selectedVoiceOverSegments } =
                    app.rootStore.state.story;
                  return [selectedAssets, selectedVoiceOverSegments];
                },
                () => {
                  this.story.hasBeenChanged();
                },
                {
                  deep: true,
                }
              )
            );

            this._storeUnwatchers.push(
              app.rootStore.watch(
                () => {
                  return app.rootStore.state.story.storyBackgroundMusic;
                },
                () => {
                  this.story.invalidateFullPreview();
                  this.story.hasBeenChanged();
                }
              )
            );

            workPool.job.checkForExpiredBGMusic.execute();
          }

          resolve();
        })
        .catch((bug) => {
          app.doctorController.report('StoryController::STORY_INIT', bug);
          reject();
        });

      this.storyView = new MainStoryView();
      this.storyMetadataView = new StoryMetadataView({
        model: this.story,
        controller: this,
      });
    });
  },

  /**
   * Persist story model to server and navigate into it
   * @param storyData
   */
  startNewStory: function (storyData) {
    app.delayedSpinner.show();
    const storyOptions = {
      showError: false,
      isNewStory: true,
    };

    app.api.draftStories
      .createNew(storyData, storyOptions)
      .done((response) => {
        const newStoryId = response.id;
        if (_.isDefined(newStoryId)) {
          app.navigate(`/stories/${newStoryId}`, { trigger: false });
          app.controller.routeStories(newStoryId, { storyOptions });
        } else {
          app.doctorController.report('Missing story.id after story creation!');
        }
      })
      .fail((response) => {
        app.delayedSpinner.hide();
        app.dialog.serverError(response).then(() => {
          app.navigateToDashboard({ replace: true });
        });
      });
  },

  /**
   * @param storyModelState {Backbone.Model}
   * @param isDirty {bool}
   * @private
   */
  _handleStoryChange: function (storyModelState, isDirty) {
    if (app.rootStore.getters['user/useAutosave'] && isDirty) {
      this._autosave(this.story);
    }
  },

  /**
   * @param story {app.namespace.Model.StoryModel}
   * @private
   */
  _autosave: function (story) {
    const currentStoryData = story.serialize();
    const autosaveStore = story.modelState.get('autosaveStore');
    const changedViaAutosave = !_.isEqual(currentStoryData, autosaveStore);

    if (changedViaAutosave) {
      app.api.draftStoriesAutosave
        .send(story, currentStoryData)
        .done((sentPackage) => {
          // @note: currentStoryData should be equal to sentPackage.currentStoryData
          story.persistStory({
            data: sentPackage.currentStoryData,
            original: false,
          });
          story.modelState.set({
            timeOfLastAutosave: Date.now(),
          });
        })
        .fail((e) => {
          app.doctorController.report(`_autosave fail: error: ${e.error}`);
        });
    }
  },

  /**
   * Exit a task
   * Receives options (obj):
   * save (bool) = If to save before exit (must be halfway validated!)
   * spinner (bool) = auto show|hide app delayed spinner
   * @returns {$.Deferred} exit promise
   */
  exit: finishStory(function (options) {
    const exitPromise = new $.Deferred(); // used to determine the entire exit process success
    const confirmPromise = new $.Deferred(); // used to determine if user confirmed exit

    options = $.extend(
      {
        save: false,
        spinner: true,
        type: 'exit', // 'exit' or 'logout'
        navigationOverride: false, // used to override navigation at the end of exit (in order to set it by callee)
      },
      options
    );

    if (
      this.story.modelState.get('isDirty') &&
      !app.rootStore.getters['user/hasPermission']('USE_AUTO_SAVE')
    ) {
      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_2,
          title: 'Your work is not saved',
          buttonNoText: options.type === 'logout' ? 'Logout' : 'Exit',
          buttonYesText:
            options.type === 'logout' ? 'Save & Logout' : 'Save & Exit',
        })
        .then((data) => {
          if (data.action === null) {
            confirmPromise.reject();
          } else {
            options.save = data.action;
            confirmPromise.resolve();
          }
        });
    } else {
      confirmPromise.resolve();
    }

    confirmPromise
      .done(() => {
        console.log('\u26A1EXIT\u26A1', options);

        if (options.spinner) {
          app.delayedSpinner.show({ delay: 0 });
        }

        const savePromise = new _.ColdPromise();

        if (options.save) {
          this.save().then(savePromise.resolve, savePromise.reject);
        } else {
          savePromise.resolve();
        }

        savePromise.then(() => {
          app.api.draftStories
            .lockOFF(this.story.id)
            .then(exitPromise.resolve, (...args) => {
              console.log('args', ...args);
              exitPromise.reject();
            })
            .always(() => {
              if (options.spinner && !/logout/.test(options.type)) {
                app.delayedSpinner.hide();
              }
            });
        }, exitPromise.reject);
      })
      .fail(exitPromise.reject);

    exitPromise.done(function () {
      if (!options.navigationOverride) {
        app.state.enableNavigation();
        if ('logout' === options.type) {
          app.rootStore.dispatch('user/logout');
        } else {
          // exit
          app.navigateToDashboard();
        }
      }
    });

    return exitPromise.promise();
  }),

  /**
   * @return {jQuery.Deferred}
   */
  save: function () {
    return new Promise((resolve, reject) => {
      const storyData = this.story.serialize();
      app.api.draftStories
        .manualSave(storyData)
        .done(() => {
          this.story.persistStory({ data: storyData, original: false });
          app.trigger('story:saved', storyData.id);
          resolve();
        })
        .fail(reject);
    });
  },

  /**
   * Prepare story for AI by setting title, saving and locking it off
   * @param {Object} options - Configuration options
   * @param {string} options.storyId - The story ID
   * @param {string} options.title - Title for the operation (default: 'preparing for ai...')
   * @returns {Promise}
   */
  prepareLockForAI: function (options = {}) {
    const {
      storyId,
      title = 'preparing for ai...',
      changeTitle = true,
    } = options;

    return new Promise((resolve, reject) => {
      // First set the title
      if (changeTitle) {
        this.story.set('title', title);
      }

      // Then save the story
      this.save()
        .then(() => {
          this.freeStory();
          app.state.enableNavigation();
          return app.api.draftStories.lockOFF(storyId);
        })
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  previewStory: function () {
    storyViewBus.$emit('svbPauseRcp');
    this._validateForProduce().done((validateOptions) => {
      this.produce(validateOptions);
    });
  },

  updateAnalyticsWithSegmentsEndOnBeat: function () {
    const story = app.rootStore.state.story;
    let bgMusic = story.bgMusicBeats;
    const visualTracks = this.story.attributes?.timelineData?.visualTracks;

    if (
      !story.bgMusicBeats.length ||
      !visualTracks ||
      story.audioTracks.length
    ) {
      return;
    }

    const storyEndTime = visualTracks[visualTracks.length - 1].displayEndTime;
    const lastItemIndex = findUpperBoundIndexInSorted(
      storyEndTime,
      0,
      bgMusic.length - 1,
      bgMusic
    );
    bgMusic = bgMusic.slice(0, lastItemIndex + 1);
    let endsOnBeat = 0;
    const cmp = (track) => {
      if (
        _.findIndexInSorted(bgMusic, (val) => {
          if (val.time < track.displayEndTime) return 1;
          if (val.time > track.displayEndTime) return -1;
          return 0;
        })
      ) {
        endsOnBeat++;
      }
    };

    visualTracks.forEach(cmp);

    endsOnBeat = (100 * endsOnBeat) / visualTracks.length;

    app.analytics('analytics:publishScreen:segmentsEndOnBeat', {
      'percentage ending on beat': Number(endsOnBeat.toFixed(1)),
      'visual tracks': visualTracks.length,
    });
  },

  produce: finishStory(function (options) {
    const rv = new $.Deferred();
    const storyId = this.story.get('id');
    const externalStoryEntryId = this.story.get('_externalStoryEntryId');

    try {
      this.updateAnalyticsWithSegmentsEndOnBeat();
    } catch (ignore) {
      /* ...*/
    }

    this._produce(options).then(
      () => {
        rv.resolve();

        app.state.enableNavigation();

        if (app.isEmbedded) {
          app.navigate2Shortcut({
            route: 'proxy-relay',
            params: {
              videoId: storyId,
              externalStoryEntryId,
            },
          });
        } else {
          app.navigate(`videos/producing/${storyId}`, { trigger: true });
        }
      },
      (err) => {
        app.dialog.serverError(err).then(rv.reject);
      }
    );

    return rv.promise();
  }),

  /**
   * @param options
   * @returns {$.Deferred}
   */
  _produce(options) {
    options = $.extend(
      {
        spinner: true,
      },
      options
    );

    const rv = new $.Deferred();

    if (options.spinner) {
      app.delayedSpinner.show();
    }

    if (_.isFinite(options.snapshotTime)) {
      this.story.set({
        videoPosterAsset: null,
        videoPosterSnapshotTime: options.snapshotTime,
      });
    }

    let videosWithTranscript =
      app.rootStore.getters['timemachine/visualsWithExistingTranscript'];
    videosWithTranscript = _.filter(videosWithTranscript, (asset) => {
      return asset.agd.showTranscription;
    });

    if (
      app.rootStore.state.story.enableVOSubtitles &&
      !app.rootStore.getters.hasAudioOnTimeline &&
      _.isEmpty(videosWithTranscript)
    ) {
      app.rootStore.commit('setEnableVOSubtitles', false);
    }

    this.save().then(() => {
      console.log('\u26A1PRODUCE\u26A1');
      app.api.draftStories.produce(this.story.id).then(rv.resolve, rv.reject);
    }, rv.reject);

    rv.always(() => {
      app.delayedSpinner.hide();
    });

    return rv.promise();
  },

  delete: finishStory(function (options) {
    options = $.extend(
      {
        spinner: true,
      },
      options
    );
    console.log('\u26A1DELETE\u26A1');
    const rv = new $.Deferred();

    // Capture the version group (if any) BEFORE deletion so we can refresh the
    // storyVersions cache once the story is gone - otherwise a stale group
    // survives and the drafts grid shows an inflated version count.
    const deletedId = this.story.get('id');
    const versionGroup =
      app.rootStore.getters['storyVersions/groupByStoryId'](deletedId);
    const versionFolderId = versionGroup ? versionGroup.folderId : null;

    app.dialog
      .confirm({
        type: app.dialog.types.CONFIRM_2,
        title: app.culture.deletePopup.message,
        buttonYesText: app.culture.deletePopup.yes,
        buttonNoText: app.culture.generic.CANCEL,
      })
      .then((data) => {
        if (!data.action) {
          return;
        }

        app.state.enableNavigation();
        const id = this.story.get('id');

        if (options.spinner) {
          app.delayedSpinner.show();
        }

        app.api.draftStories
          .delete({ id, template: this.story.get('template') })
          .then(rv.resolve, rv.reject)
          .always(() => {
            if (options.spinner) {
              app.delayedSpinner.hide();
            }
          });
      });

    rv.done(() => {
      if (versionFolderId) {
        app.rootStore.dispatch('storyVersions/REFRESH_AFTER_DELETE', {
          deletedStoryId: deletedId,
          folderId: versionFolderId,
        });
      }
      app.navigateToDashboard({ replace: true });
    });

    rv.fail((err) => {
      // 423: a version of the group is being edited by another user; nothing
      // was deleted. Stay in the editor (navigation lives in rv.done) and
      // refresh the group so lock flags are current.
      if (err && err.errorCode === 423) {
        app.dialog.error({
          type: app.dialog.types.ERROR_1,
          title: app.culture.storyVersions.deleteLocked,
        });
        if (versionFolderId) {
          app.rootStore.dispatch('storyVersions/FETCH_VERSIONS', {
            storyId: deletedId,
            folderId: versionFolderId,
            force: true,
          });
        }
      }
    });

    return rv.promise();
  }),

  setAsStoryTemplate: function () {
    app.dialog
      .confirm({
        type: app.dialog.types.CONFIRM_2,
        title: app.culture.setAsStoryTemplateDialog.setTemplateTitle,
        buttonYesText: app.culture.setAsStoryTemplateDialog.setTemplateConfirm,
      })
      .then((data) => {
        if (data.action) {
          this.doSetAsStoryTemplate();
        }
      });
  },

  /**
   * Mark story as a template
   * @return {Promise}
   */
  doSetAsStoryTemplate: function () {
    app.delayedSpinner.show();

    const data = {
      storyId: this.story.get('id'),
      categoryIds: [],
      channelId: app.rootStore.state.user.channelId,
    };
    const setAsStoryboardPromise = app.api.draftStoryTemplates
      .convertStory2Template(data)
      .then(() => {
        this.story.set({
          template: true,
          _categoryIds: [],
          _shortcutEnabled: true,
        });
        app.rootStore.commit('setStoryTemplate', true);
        app.analytics('story:setAsStoryboard');

        _.each(app.timelineGateway.getVisualData(), (asset) => {
          app.setDefaultAssetItemSchemaModel({ asset });
        });
        app.timelineGateway.serializeTimeline();

        return app.storyController.save();
      });

    setAsStoryboardPromise
      .then(() => {
        app.delayedSpinner.hide();
      })
      .catch(() => {
        app.delayedSpinner.hide();
        app.dialog.error({
          type: app.dialog.types.ERROR_1,
          title: app.culture.setAsStoryTemplateDialog.setTemplateFailedTitle,
          description:
            app.culture.setAsStoryTemplateDialog.setTemplateFailedDesc,
          buttonCloseText: app.culture.generic.CLOSE,
          showContactCsm: false,
        });
      });
  },

  unsetStoryTemplate: function () {
    app.dialog
      .confirm({
        type: app.dialog.types.CONFIRM_2,
        title: app.culture.setAsStoryTemplateDialog.unsetTemplateTitle,
        buttonYesText:
          app.culture.setAsStoryTemplateDialog.unsetTemplateConfirm,
      })
      .then((data) => {
        if (data.action) {
          app.delayedSpinner.show();
          const storyId = this.story.get('id');
          app.api.draftStoryTemplates
            .convertTemplate2Story(storyId)
            .then(() => {
              _.each(app.timelineGateway.getVisualData(), (asset) => {
                app.unsetAssetItemSchemaModel({ asset });
              });
              this.story.hasBeenChanged();
              app.timelineGateway.serializeTimeline();
            })
            .then(() => {
              app.delayedSpinner.hide();
              this.story.set('template', false);
              app.rootStore.commit('setStoryTemplate', false);
            })
            .catch(() => {
              app.delayedSpinner.hide();
              app.dialog.error({
                type: app.dialog.types.ERROR_1,
                title:
                  app.culture.setAsStoryTemplateDialog.unsetTemplateFailedTitle,
                description:
                  app.culture.setAsStoryTemplateDialog.unsetTemplateFailedDesc,
                buttonCloseText: app.culture.generic.CLOSE,
                showContactCsm: false,
              });
            });
        }
      });
  },

  copyDraftToDraft: function () {
    const savedPromise = new _.ColdPromise();

    if (
      !app.rootStore.getters['user/useAutosave'] &&
      this.story.modelState.get('isDirty')
    ) {
      app.delayedSpinner.show();
      this.save().then(() => {
        app.delayedSpinner.hide();
        savedPromise.resolve();
      });
    } else {
      savedPromise.resolve();
    }

    savedPromise.then(() => {
      DuplicateController.openDialog({
        story: this.story.attributes,
        source: 'timeline',
        message: app.culture.makeCopyDialog.messageStory,
        storyStartPoint: app.storyStartPoint.DUPLICATE_IN_PROGRESS_STORY,
      }).then((data) => {
        if (data.changeOrg) {
          app.delayedSpinner.hide();
        } else {
          app.api.draftStories.lockOFF(this.story.id).then(() => {
            this.freeStory();
            app.state.enableNavigation();
            app.navigate(`stories/${data.storyId}`);
          });
        }
      });
    });
  },

  translate: function () {
    const timelineData = this.story.getTimelineData();
    const segments = timelineData.visualTracks;

    app.showModal({
      type: 'small',
      component: 'wt-translate-story',
      componentPropsData: {
        segments,
        message: app.culture.translateStoryDialog.message,
        programmerLanguage: this.story.getProgrammer().get('language'),
      },
    });
  },

  /**
   * Convert story timeline to opposite type
   * @return {Promise}
   */
  changeStyle: function ({ targetProgrammerId, brandId, themeId }) {
    return new Promise((resolve, reject) => {
      const storyId = this.story.id;
      const storyOptions = { refreshPreview: true };

      app.delayedSpinner.show();
      this.story.cancelAutosave(true);
      this.story.hasBeenChanged.cancel();

      this.save().then(() => {
        app.api.draftStories
          .changeStoryBrand({
            storyId,
            targetProgrammerId,
            brandId,
            themeId,
          })
          .then(() => {
            this.freeStory();
            app.state.enableNavigation();
            app.controller.routeStories(storyId, { storyOptions });
            resolve();
          }, reject);
      }, reject);
    });
  },

  _validateForProduce: function () {
    const allValid = new $.Deferred();
    const resolveObj = {};

    // NOTE: dealing with synchronous and then deferred validation
    if (this.storyMetadataView.validateForProduce()) {
      this._validateForSkipPreview()
        .done((previewResolveObj) => {
          Object.assign(resolveObj, previewResolveObj);
          this._validateForGaps()
            .done(() => {
              allValid.resolve(resolveObj);
            })
            .fail(allValid.reject);
        })
        .fail(allValid.reject);
    } else {
      allValid.reject();
    }

    return allValid.promise();
  },

  _validateForSkipPreview: function (options) {
    options = $.extend(
      {
        snapshotTime: null,
      },
      options
    );

    const skipPreviewValid = new $.Deferred();
    this.generatePreview()
      .done(function (previewObj) {
        skipPreviewValid.resolve(
          Object.assign(options, { snapshotTime: previewObj.snapshotTime })
        );
      })
      .fail(function () {
        skipPreviewValid.reject(options);
      });

    return skipPreviewValid.promise();
  },

  _validateForGaps: function () {
    const allGapsValid = new $.Deferred();

    if (app.rootStore.getters['timemachine/hasVisualGap']) {
      storyViewBus.$emit('svbShowVisualAudioGap');

      app.dialog
        .error({
          title: app.culture.alert.TIMELINE_HAS_GAPS_TITLE,
          description: app.culture.alert.TIMELINE_HAS_GAPS,
        })
        .then(allGapsValid.reject);
    } else {
      allGapsValid.resolve();
    }

    return allGapsValid.promise();
  },

  isStoryInPublishingStateByTaskType: function (taskType) {
    return (
      taskType === app.ROLES.PUBLISH || taskType === app.ROLES.VIDEO_RENDERING
    );
  },

  generatePreview: function () {
    const generatePreviewPromise = new $.Deferred();

    this.validateForPreview()
      .done(() => {
        app
          .showModal({
            type: 'normal',
            closeOnClickOut: false,
            showCloseButton: false,
            component: this.story.get('template')
              ? StoryboardFullPreview
              : FullPreview,
            store: app.rootStore,
            componentPropsData: {
              story: this.story,
              isStoryboarder:
                app.rootStore.getters['user/isTemplateSpecialist'],
            },
          })
          .$on('close', (action) => {
            _.isObject(action) && action.produce
              ? generatePreviewPromise.resolve(action)
              : generatePreviewPromise.reject();
          });
      })
      .fail(generatePreviewPromise.reject);

    return generatePreviewPromise.promise();
  },

  validateForPreview: function () {
    const rv = new $.Deferred();

    // validate that all tracks are not empty
    const timelineData = this.story.getTimelineData();
    const allTracksAreEmpty = _.isEmpty(timelineData.visualTracks);

    if (allTracksAreEmpty) {
      app.dialog
        .error({
          title: app.culture.alert.TIMELINE_IS_EMPTY_TITLE,
          description: app.culture.alert.TIMELINE_IS_EMPTY_DESC_PREVIEW,
        })
        .then(rv.reject);
    } else if (
      !app.validate.storyDuration(
        app.rootStore.state.story.timelineDuration,
        this.story
      )
    ) {
      const durationTxt = app.moment
        .duration(this.story.getProgrammer().get('maxStoryDuration'), 'seconds')
        .humanize();
      app.dialog
        .error({
          type: app.dialog.types.ERROR_1,
          title: app.culture.alert.STORY_TOO_LONG_TITLE,
          description:
            app.culture.alert.STORY_TOO_LONG_TIMELINE_DESC2.format(durationTxt),
          buttonCloseText: 'OK',
          showContactCsm: true,
          csmSentenceText: '',
          csmChatMessage:
            app.culture.alert.STORY_TOO_LONG_TIMELINE_CSM.format(durationTxt),
        })
        .then(() => {
          rv.reject();
        });
    } else if (
      !app.remoteStash.get('hideSoftDurationValidation') &&
      !app.validate.storyDurationSoft(
        app.rootStore.state.story.timelineDuration,
        this.story
      )
    ) {
      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_3,
          title: app.culture.alert.STORY_DURATION_SOFT_TITLE,
          description: app.culture.alert.STORY_DURATION_SOFT_DESC.format(
            app.moment
              .duration(
                this.story.getProgrammer().get('storySoftMaxDuration'),
                'seconds'
              )
              .humanize()
          ),
          buttonYesText: app.culture.generic.IMREADY,
          buttonNoText: app.culture.generic.CANCEL,
          checkboxLabel: app.culture.confirmPopup.doNotShowAgain,
          showCheckbox: true,
        })
        .then((data) => {
          if (data.action) {
            if (data.metadata.checkboxValue) {
              app.remoteStash.set('hideSoftDurationValidation', true);
            }
            rv.resolve();
          } else {
            rv.reject();
          }
        });
    } else {
      rv.resolve();
    }

    return rv.promise();
  },

  /**
   * @param asset {app.namespace.Model.AssetModel|Object}
   * @return {boolean}
   */
  isVideoPoster: function (asset) {
    let rv = false;

    if (this.story && asset) {
      // can happen when user exits a story
      const posterModel = this.story.videoPosterModel;
      asset = asset.attributes || asset;

      if (!this.story.isVideoPosterEmpty()) {
        rv = posterModel.getMetadata('assetUUID') === asset.metadata.assetUUID;
        if (rv && app.assetType.isFRAGMENT(asset)) {
          rv =
            posterModel.get('startTime') === asset.startTime &&
            posterModel.get('endTime') === asset.endTime;
        }
      }
    }

    return rv;
  },

  _executeMenuCommand: function (command) {
    if (!this.story) {
      return;
    }

    storyViewBus.$emit('svbPauseRcp');

    switch (command) {
      case 'DELETE':
        this.delete();
        break;

      case 'TRANSLATE':
        this.translate();
        break;

      case 'COPY_DRAFT_TO_DRAFT':
        this.copyDraftToDraft();
        break;

      case 'SET_AS_TEMPLATE':
        this.setAsStoryTemplate();
        break;

      case 'UNSET_TEMPLATE':
        this.unsetStoryTemplate();
        break;

      case 'VERSION_HISTORY':
        this.openVersionHistory();
        break;

      default:
        app.doctorController.report(`Menu ${command} not supported`);
    }
  },

  openVersionHistory: function () {
    const storyId = this.story.get('id');
    app.rootStore
      .dispatch('storyVersions/FETCH_VERSIONS', { storyId, force: true })
      .catch(() => {});
    app.rootStore.commit('storyVersions/setPanelOpen', true);
  },

  /**
   * Switch the editor to another version of the current story family. Releases
   * the current lock first, then opens the target (whose lockON flips the
   * group's active version server-side). Modeled on copyDraftToDraft's
   * navigation flow.
   * @param {string} targetStoryId
   */
  switchToVersion: function (targetStoryId) {
    if (!targetStoryId || targetStoryId === this.story.id) {
      return;
    }

    const savedPromise = new $.Deferred();

    if (
      !app.rootStore.getters['user/useAutosave'] &&
      this.story.modelState.get('isDirty')
    ) {
      app.delayedSpinner.show();
      this.save().then(() => {
        app.delayedSpinner.hide();
        savedPromise.resolve();
      });
    } else {
      savedPromise.resolve();
    }

    savedPromise.then(() => {
      app.rootStore.commit('storyVersions/setPanelOpen', false);
      app.api.draftStories.lockOFF(this.story.id).then(() => {
        this.freeStory();
        app.state.enableNavigation();
        app.navigate(`stories/${targetStoryId}`);
      });
    });
  },
});
